import * as _ from "lodash-es";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState
} from "mantine-react-table-open";
import { Fragment, useMemo } from "react";
import { getDefaultMRTOptions } from "../../../../../../../../mantine/getDefaultMRTOptions";
import type { Measurand } from "../../../../../../../../utils/backend-types";
import type { Frequency } from "../../../../../../../../utils/enums";
import type { DataGapsTableObject } from "../DataGaps.types";
import { getDateRangeString } from "../utils/getDateRangeString";
import { getHourRangeString } from "../utils/getHourRangeString";
import { mapMeasurandToLabel } from "../utils/mapMeasurandToLabel";

export interface DataGapsTableProps {
  data: Array<DataGapsTableObject>;
  frequency: Frequency;
}

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "date",
    desc: true
  }
];

function DataGapsTable({ data, frequency }: DataGapsTableProps) {
  const columns = useMemo<Array<MRT_ColumnDef<DataGapsTableObject>>>(
    () => [
      {
        accessorKey: "date",
        header: "Datum",
        Cell: ({ cell }) => {
          const date = cell.getValue<{
            startDate: DateTime;
            lastDate: DateTime;
          }>();
          return <>{getDateRangeString(date.startDate, date.lastDate)}</>;
        }
      },
      {
        accessorKey: "dataGaps",
        header: "Zeitraum",
        Cell: ({ row }) => (
          <div className="data-gaps-table-timeseries-container">
            {row.original.dataGaps.map((gap, index) => {
              return (
                <Fragment key={"hourGap" + index}>
                  {getHourRangeString(
                    DateTime.fromISO(gap[0], { zone: "Europe/Berlin" }),
                    DateTime.fromISO(gap[1], { zone: "Europe/Berlin" }),
                    frequency
                  )}
                  <br />
                </Fragment>
              );
            })}
          </div>
        )
      },
      {
        accessorKey: "measurand",
        header: "Messrichtung",
        Cell: ({ row }) => (
          <>{mapMeasurandToLabel(row.original.measurand as Measurand)}</>
        )
      }
    ],
    [frequency]
  );

  const defaultOptions = getDefaultMRTOptions<DataGapsTableObject>({
    emptyRowsFallbackText: "Keine Datenlücken vorhanden!",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 15 },
      sorting: DEFAULT_SORTED
    },
    hideBorder: true
  });

  const tableOptions = _.merge({}, defaultOptions, {
    enableColumnActions: true
  });

  const table = useMantineReactTable({
    ...tableOptions,
    columns,
    data: data
  });

  return (
    <div className="DataGapsTable">
      <MantineReactTable table={table} />
    </div>
  );
}

export { DataGapsTable };
